import api from "@/lib/axiosConfig";
import { type SupportedLocale } from "@/i18n";
import { BASE } from "@/api/cms-puck/client";
import type {
  NavMenu,
  NavMenuKey,
  ResolvedMenuItem,
  UpsertNavMenuItemInput,
} from "@/api/cms-puck/types";

/* -------------------------------------------------------------------------- */
/* Menus (nav header/footer)                                                  */
/* -------------------------------------------------------------------------- */

/** Menu d'authoring par clé (auto-créé côté API si absent). */
export async function getMenu(key: NavMenuKey): Promise<NavMenu> {
  const { data } = await api.get<NavMenu>(
    `${BASE}/menus/${encodeURIComponent(key)}`,
  );
  return data;
}

/** Menu résolu pour l'aperçu éditeur (inclut les pages brouillon). */
export async function resolveMenuPreview(
  key: NavMenuKey,
  lang: SupportedLocale,
): Promise<ResolvedMenuItem[]> {
  const { data } = await api.get<ResolvedMenuItem[]>(
    `${BASE}/menus/${encodeURIComponent(key)}/resolve/${encodeURIComponent(lang)}`,
  );
  return Array.isArray(data) ? data : [];
}

export async function createMenuItem(
  key: NavMenuKey,
  input: UpsertNavMenuItemInput,
): Promise<NavMenu> {
  const { data } = await api.post<NavMenu>(
    `${BASE}/menus/${encodeURIComponent(key)}/items`,
    input,
  );
  return data;
}

export async function updateMenuItem(
  itemId: string,
  input: UpsertNavMenuItemInput,
): Promise<NavMenu> {
  const { data } = await api.put<NavMenu>(
    `${BASE}/menus/items/${encodeURIComponent(itemId)}`,
    input,
  );
  return data;
}

export async function deleteMenuItem(itemId: string): Promise<NavMenu> {
  const { data } = await api.delete<NavMenu>(
    `${BASE}/menus/items/${encodeURIComponent(itemId)}`,
  );
  return data;
}

/** Réordonne / re-parente tous les items d'un menu en une passe. */
export async function reorderMenu(
  key: NavMenuKey,
  items: { id: string; parentId?: string | null; position: number }[],
): Promise<NavMenu> {
  const { data } = await api.put<NavMenu>(
    `${BASE}/menus/${encodeURIComponent(key)}/reorder`,
    { items },
  );
  return data;
}

/** Labels localisés d'un item (`{ fr, en, … }`). */
export async function getMenuItemLabels(
  itemId: string,
): Promise<Partial<Record<SupportedLocale, string>>> {
  const { data } = await api.get<Partial<Record<SupportedLocale, string>>>(
    `${BASE}/menus/items/${encodeURIComponent(itemId)}/labels`,
  );
  return data;
}

/** Définit les labels localisés d'un item (vide = retour au titre de page). */
export async function setMenuItemLabels(
  itemId: string,
  labels: Partial<Record<SupportedLocale, string>>,
): Promise<Partial<Record<SupportedLocale, string>>> {
  const { data } = await api.put<Partial<Record<SupportedLocale, string>>>(
    `${BASE}/menus/items/${encodeURIComponent(itemId)}/labels`,
    { labels },
  );
  return data;
}
